import type { AxiosError } from "axios";
import axios from "./axios";
import { ErrorResponse } from "../errors/errorHandler";
import { ApiResponse } from "../types/express-data.type";
import {
  CustomerOptionsResponse,
  PivotCustomerOptionsParams,
  PivotExportParams,
  PivotProductStoresParams,
  PivotRouteDayDetailParams,
  PivotRouteDaysParams,
  PivotProductsParams,
  PivotRollupStatus,
  PivotRollupStatusParams,
  PivotScopeParams,
  PivotStoresParams,
  PivotVisitsParams,
  ProductPivotResponse,
  ProductStoresResponse,
  RouteDayDetailResponse,
  RouteDaysResponse,
  StorePivotResponse,
  VisitPivotResponse,
  VisitTimesResponse,
} from "../types/pivot.type";

/**
 * The aggregations are slow by nature, and slowest on exactly the request this feature
 * exists to explain: head office, one month, product view, answered live rather than from
 * the daily rollup, measured at 43s. The backend's own `statement_timeout` is 60s, so it
 * always answers or fails inside a minute — this leaves room above that so a *working*
 * request is never turned into a client-side network error.
 *
 * The shared instance sets no timeout at all (Axios default: wait forever). That is the
 * other failure to avoid: a dropped connection would leave the table spinning with nothing
 * to show and nothing to retry.
 */
const PIVOT_TIMEOUT_MS = 70_000;

// `customer_type` / `customer_group` are OpenAPI `style: form, explode: true` — repeat the
// key once per value (`?customer_type=A&customer_type=B`), not comma-joined.
const appendScope = (params: URLSearchParams, scope: PivotScopeParams) => {
  params.append("date_from", scope.date_from);
  params.append("date_to", scope.date_to);
  if (scope.timezone) params.append("timezone", scope.timezone);
  if (scope.branch_code) params.append("branch_code", scope.branch_code);
  if (scope.route_code) params.append("route_code", scope.route_code);
  scope.customer_type?.forEach((v) => params.append("customer_type", v));
  scope.customer_group?.forEach((v) => params.append("customer_group", v));
};

export const getPivotStores = (params: PivotStoresParams) => {
  const query = new URLSearchParams();
  appendScope(query, params);
  if (params.group_by) query.append("group_by", params.group_by);
  if (params.q) query.append("q", params.q);
  if (params.sort_by) query.append("sort_by", params.sort_by);
  if (params.sort_dir) query.append("sort_dir", params.sort_dir);
  if (params.rows_per_group)
    query.append("rows_per_group", String(params.rows_per_group));
  if (params.group_limit) query.append("group_limit", String(params.group_limit));

  return axios.get<ApiResponse<StorePivotResponse>>(
    `/pivot/stores?${query.toString()}`,
    { timeout: PIVOT_TIMEOUT_MS },
  );
};

export const getPivotProducts = (params: PivotProductsParams) => {
  const query = new URLSearchParams();
  appendScope(query, params);
  if (params.customer_code) query.append("customer_code", params.customer_code);
  params.product_code?.forEach((v) => query.append("product_code", v));
  if (params.q) query.append("q", params.q);
  if (params.sort_by) query.append("sort_by", params.sort_by);
  if (params.sort_dir) query.append("sort_dir", params.sort_dir);
  if (params.rows_per_group) {
    query.append("rows_per_group", String(params.rows_per_group));
    // 1-based and per group, and the backend ignores it without a page size — so only send
    // it alongside one, never on its own.
    if (params.page) query.append("page", String(params.page));
  }

  return axios.get<ApiResponse<ProductPivotResponse>>(
    `/pivot/products?${query.toString()}`,
    { timeout: PIVOT_TIMEOUT_MS },
  );
};

/** One product's stores, grouped by channel — the product row's inline drill. */
export const getPivotProductStores = (params: PivotProductStoresParams) => {
  const query = new URLSearchParams();
  appendScope(query, params);
  if (params.customer_code) query.append("customer_code", params.customer_code);
  query.append("product_code", params.product_code);
  if (params.q) query.append("q", params.q);
  if (params.sort_by) query.append("sort_by", params.sort_by);
  if (params.sort_dir) query.append("sort_dir", params.sort_dir);
  if (params.rows_per_group)
    query.append("rows_per_group", String(params.rows_per_group));
  if (params.group_limit) query.append("group_limit", String(params.group_limit));

  return axios.get<ApiResponse<ProductStoresResponse>>(
    `/pivot/products/stores?${query.toString()}`,
    { timeout: PIVOT_TIMEOUT_MS },
  );
};

export const getPivotVisits = (params: PivotVisitsParams) => {
  const query = new URLSearchParams();
  appendScope(query, params);
  if (params.group_by) query.append("group_by", params.group_by);
  if (params.gap_min_days) query.append("gap_min_days", String(params.gap_min_days));
  if (params.grid_max_days)
    query.append("grid_max_days", String(params.grid_max_days));
  if (params.q) query.append("q", params.q);
  if (params.sort_by) query.append("sort_by", params.sort_by);
  if (params.sort_dir) query.append("sort_dir", params.sort_dir);
  if (params.rows_per_group)
    query.append("rows_per_group", String(params.rows_per_group));
  if (params.group_limit) query.append("group_limit", String(params.group_limit));

  return axios.get<ApiResponse<VisitPivotResponse>>(
    `/pivot/visits?${query.toString()}`,
    { timeout: PIVOT_TIMEOUT_MS },
  );
};

/** PWA visit-time medians per store, for the สายส่ง tab's time columns (board A3). */
export const getPivotVisitTimes = (params: PivotScopeParams) => {
  const query = new URLSearchParams();
  appendScope(query, params);
  return axios.get<ApiResponse<VisitTimesResponse>>(`/pivot/visit-times?${query.toString()}`, {
    timeout: PIVOT_TIMEOUT_MS,
  });
};

/** Route-day list for the เวลาวิ่งสาย tab (board A1). Span ≤ 31 days. */
export const getPivotRouteDays = (params: PivotRouteDaysParams) => {
  const query = new URLSearchParams();
  query.append("date_from", params.date_from);
  query.append("date_to", params.date_to);
  if (params.timezone) query.append("timezone", params.timezone);
  if (params.branch_code) query.append("branch_code", params.branch_code);
  if (params.route_code) query.append("route_code", params.route_code);
  if (params.only_flagged) query.append("only_flagged", "true");
  return axios.get<ApiResponse<RouteDaysResponse>>(`/pivot/route-days?${query.toString()}`, {
    timeout: PIVOT_TIMEOUT_MS,
  });
};

/** One route-day's stops, for the timeline (board A2). */
export const getPivotRouteDayDetail = (params: PivotRouteDayDetailParams) => {
  const query = new URLSearchParams();
  query.append("route_code", params.route_code);
  query.append("date", params.date);
  if (params.timezone) query.append("timezone", params.timezone);
  return axios.get<ApiResponse<RouteDayDetailResponse>>(
    `/pivot/route-days/detail?${query.toString()}`,
    { timeout: PIVOT_TIMEOUT_MS },
  );
};

export const getPivotCustomerOptions = (params: PivotCustomerOptionsParams) => {
  const query = new URLSearchParams();
  if (params.branch_code) query.append("branch_code", params.branch_code);
  if (params.route_code) query.append("route_code", params.route_code);
  params.customer_type?.forEach((v) => query.append("customer_type", v));
  params.customer_group?.forEach((v) => query.append("customer_group", v));
  if (params.q) query.append("q", params.q);
  if (params.limit) query.append("limit", String(params.limit));

  return axios.get<ApiResponse<CustomerOptionsResponse>>(
    `/pivot/customers?${query.toString()}`,
  );
};

/**
 * Whether a rollup rebuild is running, and how far along.
 *
 * The only thing that knows: a pivot response's `refresh.state` says what *that request*
 * asked for, and a cluster-wide lock can deflect the attempt to a run already in progress.
 * Cheap enough to poll — one lock scan and one indexed count, no aggregation — so it takes
 * the normal timeout rather than the pivot one.
 */
export const getPivotRollupStatus = (params: PivotRollupStatusParams = {}) => {
  const query = new URLSearchParams();
  if (params.branch_code) query.append("branch_code", params.branch_code);
  const qs = query.toString();

  return axios.get<ApiResponse<PivotRollupStatus>>(
    `/pivot/rollup/status${qs ? `?${qs}` : ""}`,
    // A failed poll is not worth a toast: the banner it drives is already on screen saying
    // the numbers are live and up to date, and the next tick retries anyway. A 403 must not
    // take the page down either — this is a background nicety on a page the user has already
    // been served, so it opts out of the Permission Denied redirect as well.
    { meta: { suppressErrorToast: true, suppressForbiddenRedirect: true } },
  );
};

export const exportPivotCsv = (params: PivotExportParams) => {
  const query = new URLSearchParams();
  query.append("view", params.view);
  appendScope(query, params);
  if (params.group_by) query.append("group_by", params.group_by);
  if (params.customer_code) query.append("customer_code", params.customer_code);
  params.product_code?.forEach((v) => query.append("product_code", v));
  if (params.gap_min_days) query.append("gap_min_days", String(params.gap_min_days));
  if (params.q) query.append("q", params.q);
  if (params.sort_by) query.append("sort_by", params.sort_by);
  if (params.sort_dir) query.append("sort_dir", params.sort_dir);

  return axios.get(`/pivot/export?${query.toString()}`, {
    responseType: "blob",
    timeout: PIVOT_TIMEOUT_MS,
    meta: { suppressErrorToast: true },
  });
};

/**
 * The export asks for a Blob, so a 4xx JSON error body arrives as a Blob too — and because
 * the request opts out of the shared toast, the interceptor's rehydrate step never runs on
 * it. Read the body back here so the caller can show the backend's own message (e.g. the
 * visits-span guard) instead of failing silently.
 */
export const readPivotExportErrorMessage = async (
  error: unknown,
): Promise<string | undefined> => {
  const body = (error as AxiosError<ErrorResponse | Blob>)?.response?.data;
  if (!body) return undefined;

  if (body instanceof Blob) {
    try {
      const parsed = JSON.parse(await body.text()) as ErrorResponse;
      return parsed.message || parsed.error || parsed.detail;
    } catch {
      // Not a JSON body (proxy HTML page, empty body) — nothing to surface.
      return undefined;
    }
  }

  return body.message || body.error || body.detail;
};
